'use client'

// Clerk-free auth shims (Phase 6 / R1 cleanup, 2026-07-22).
//
// Clerk was retired at the 2026-07-14 static cutover and its packages are removed. These
// hooks keep the former Clerk-hook consumers (NavAuth, posthogProvider, favourites context,
// download CTAs, …) compiling unchanged: they return a permanently loaded, signed-out state,
// so every consumer renders its logged-out UI. The signed-in union variants exist purely so
// the consumers' signed-in code paths still typecheck — they are unreachable at runtime.
//
// R2 (Supabase identity islands) replaces this module and its consumers wholesale.

/** Minimal structural stand-in for the old Clerk UserResource fields consumers touch. */
export type AuthShimUser = {
  id: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  imageUrl: string
  emailAddresses: Array<{ emailAddress: string }>
  primaryEmailAddress: { emailAddress: string } | null
  publicMetadata: Record<string, unknown>
}

type UseUserReturn =
  | { isLoaded: true; isSignedIn: false; user: null }
  | { isLoaded: true; isSignedIn: true; user: AuthShimUser }

type UseAuthReturn = {
  isLoaded: boolean
  isSignedIn: boolean
  userId: string | null
  sessionId: string | null
  sessionClaims: Record<string, unknown> | null
  actor: null
  orgId: string | null
  orgRole: string | null
  orgSlug: string | null
  has: () => boolean
  signOut: () => Promise<void>
  getToken: () => Promise<string | null>
}

const signedOutUser: UseUserReturn = {
  isLoaded: true,
  isSignedIn: false,
  user: null,
}

const signedOutAuth: UseAuthReturn = {
  isLoaded: true,
  isSignedIn: false,
  userId: null,
  sessionId: null,
  sessionClaims: null,
  actor: null,
  orgId: null,
  orgRole: null,
  orgSlug: null,
  has: () => false,
  signOut: async () => {},
  getToken: async () => null,
}

export const useUser = (): UseUserReturn => signedOutUser
export const useAuth = (): UseAuthReturn => signedOutAuth
